"use client";

import { cn, formatPrice } from "@/lib/utils";
import Link from "next/link";
import { Button, buttonVariants } from "@/components/atoms/buttons";
import PromoCodeForm from "@/components/organisms/forms/checkout/promo-code-form";
import { __, getPermalink } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { useCustomer } from "@/hooks/useCustomer";
import { useCart } from "@/hooks/useCart";
import { toast } from "@/components/atoms/toast";

type CartSidebarProps = {
  locale: Locales;
};

const CartSidebar = ({ locale }: CartSidebarProps) => {
  const { customer } = useCustomer();
  const { cart } = useCart();

  const isCartEmpty = !cart || cart?.items?.length === 0;

  return (
    <aside className="flex flex-2/5 flex-col gap-5 max-md:pb-7.5 xl:max-w-[484px]">
      <h2 className="font-cabinet-grotesk text-3xl font-medium max-sm:hidden">{__("Estimated quote", locale)}</h2>
      <h2 className="font-cabinet-grotesk text-3xl font-medium min-sm:hidden">{__("Order summary", locale)}</h2>
      <dl className="border-primary-light/50 space-y-5 border-t pt-5">
        <div className="flex items-center justify-between">
          <dd className="text-primary-light">{__("Total VAT exc.", locale)}</dd>
          <dt>{formatPrice(cart?.subtotal || 0)}</dt>
        </div>
        {!!cart?.discount_total && (
          <div className="flex items-center justify-between">
            <dd className="text-primary-light">{__("Promo code", locale)}</dd>
            <dt> -{formatPrice(cart?.discount_total || 0)}</dt>
          </div>
        )}
        <div className="flex items-center justify-between">
          <dd className="text-primary-light">{__("VAT (21%)", locale)}</dd>
          <dt>{formatPrice(cart?.tax_total || 0)}</dt>
        </div>
        <hr className="border-primary-light/50 my-5" />
        <div className="flex items-start justify-between">
          <dd className="leading-h1 text-lg font-semibold">
            {__("Total VAT", locale)}* <br />
            <span className="text-primary-light text-xs font-normal">{__("Shipping costs not included", locale)}</span>
          </dd>
          <dt className="text-xl font-semibold">{formatPrice(cart?.total || 0)}</dt>
        </div>
      </dl>
      {customer === undefined ? null : !customer ? (
        <Button
          asChild
          className="font-semibold transition-transform duration-150 ease-in-out active:scale-95 sm:ml-auto xl:w-full"
          size="md"
        >
          <Link href={`${getPermalink(locale, "login")}?redirect=${getPermalink(locale, "checkout", "event-info")}`}>
            {__("Log in to get your quote.", locale)}
          </Link>
        </Button>
      ) : (
        <Link
          href={getPermalink(locale, "checkout", "event-info")}
          onClick={(e) => {
            if (isCartEmpty) {
              e.preventDefault();
              toast.warning({ title: __("Your cart is empty. Please add items before proceeding.", locale) });
            }
          }}
          className={cn(
            buttonVariants({ variant: "primary", size: "md" }),
            "min-h-11 font-semibold transition-transform duration-150 ease-in-out active:scale-95 sm:ml-auto xl:w-full"
          )}
        >
          {__("Next", locale)}
        </Link>
      )}
      <PromoCodeForm locale={locale} />
    </aside>
  );
};

export default CartSidebar;
